import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Row, Col } from 'react-bootstrap';
import { FiEdit, FiTrash2, FiPlus } from 'react-icons/fi';
import api from '../../api/axios';

const ProductManager = ({ businessId }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    is_active: true,
    category_id: '',
  });

  const [filtroCategoria, setFiltroCategoria] = useState('');

  const fetchProducts = async () => {
    try {
      const res = await api.get(`/products?business_id=${businessId}`);
      setProducts(res.data);
    } catch (err) {
      console.error('Error al traer productos:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get(`/categories?business_id=${businessId}`);
      setCategories(res.data);
    } catch (err) {
      console.error('Error al traer categorías:', err);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [businessId]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/products/${editingId}`, form);
      } else {
        await api.post('/products', { ...form, business_id: businessId });
      }
      setShowModal(false);
      fetchProducts();
      resetForm();
    } catch (err) {
      console.error('Error al guardar producto:', err);
    }
  };

  const handleEdit = prod => {
    setForm({
      name: prod.name,
      description: prod.description,
      price: prod.price,
      is_active: prod.is_active,
      category_id: prod.category_id,
    });
    setEditingId(prod.id);
    setShowModal(true);
  };

  const handleDelete = async id => {
    const confirm = window.confirm('¿Estás seguro de que querés eliminar este producto?');
    if (!confirm) return;
    try {
      await api.delete(`/products/${id}`);
      fetchProducts();
    } catch (err) {
      console.error('Error al eliminar producto:', err);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      price: '',
      is_active: true,
      category_id: '',
    });
    setEditingId(null);
  };

  const productosFiltrados = filtroCategoria
    ? products.filter(p => p.category_id === filtroCategoria)
    : products;

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Productos</h4>
        <Button variant="success" onClick={() => { resetForm(); setShowModal(true); }}>
          <FiPlus className="me-1" /> Nuevo producto
        </Button>
      </div>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Select
            value={filtroCategoria}
            onChange={e => setFiltroCategoria(e.target.value)}
          >
            <option value="">Todas las categorías</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Button
            variant="secondary"
            onClick={() => setFiltroCategoria('')}
          >
            Limpiar filtro
          </Button>
        </Col>
      </Row>

      <Table striped bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th>Nombre</th>
            <th>Descripción</th>
            <th>Precio</th>
            <th>Categoría</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {productosFiltrados.map(prod => (
            <tr key={prod.id}>
              <td>{prod.name}</td>
              <td>{prod.description}</td>
              <td>${prod.price}</td>
              <td>{categories.find(c => c.id === prod.category_id)?.name || '—'}</td>
              <td>{prod.is_active ? '✅ Activo' : '❌ Inactivo'}</td>
              <td>
                <Button size="sm" variant="outline-warning" className="me-2" onClick={() => handleEdit(prod)}>
                  <FiEdit className="me-1" /> Editar
                </Button>
                <Button size="sm" variant="outline-danger" onClick={() => handleDelete(prod.id)}>
                  <FiTrash2 className="me-1" /> Eliminar
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Editar producto' : 'Nuevo producto'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control name="name" value={form.name} onChange={handleChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control name="description" value={form.description} onChange={handleChange} as="textarea" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Precio</Form.Label>
              <Form.Control name="price" value={form.price} onChange={handleChange} type="number" required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Categoría</Form.Label>
              <Form.Select name="category_id" value={form.category_id} onChange={handleChange} required>
                <option value="">Seleccionar categoría</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
                label="Activo"
              />
            </Form.Group>
            <Button type="submit" variant="success">
              {editingId ? 'Actualizar' : 'Guardar'}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ProductManager;
